<template>
    <template v-for="shiftId in orderedShiftIds" :key="'withoutFunction' + shiftId">
        <LegacyWorkloadRequirementsBodyRow
            :requirement="workloadRequirements[shiftId]?.['withoutFunction'] ?? {}"
            :shift="shifts[shiftId]"
            :workloadCounts="workloadCounts"
            :roster="roster"
            :resourceFunctionNames="resourceFunctionNames"
            :negativeOnly="negativeOnly"
        />
        <template v-if="shiftId in workloadRequirements">
            <template
                v-for="(requirements, functionId) in workloadRequirements[shiftId]"
                :key="shiftId + '-' + functionId"
            >
                <LegacyWorkloadRequirementsBodyRow
                    v-if="requirements && functionId !== 'withoutFunction'"
                    :requirement="requirements"
                    :functionId="Number(functionId)"
                    :shift="shifts[shiftId]"
                    :workloadCounts="workloadCounts"
                    :roster="roster"
                    :resourceFunctionNames="resourceFunctionNames"
                    :negativeOnly="negativeOnly"
                />
            </template>
        </template>
    </template>
    <LegacyWorkloadRequirementsEditLinks :roster="roster" />
</template>

<script lang="ts">
    import LegacyWorkloadRequirementsBodyRow from '@/components/Planbord/WorkloadRequirements/LegacyWorkloadRequirementsBodyRow.vue';
    import LegacyWorkloadRequirementsEditLinks from '@/components/Planbord/WorkloadRequirements/LegacyWorkloadRequirementsEditLinks.vue';
    import {Shift, LegacyWorkloadRequirement, PersonalStampShift, PersonalStamp, OrganisationDeploymentState} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import {WorkloadRequirementSorting} from '@/types/enums/WorkloadRequirementSorting';
    import {PlanbordResource, ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import WorkloadTree from '@/util/workloadtreecreator';
    import {WorkloadCount, RosterData} from '@/types/types';
    import WorkloadCounter from '@/util/workloadcounter';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            LegacyWorkloadRequirementsBodyRow,
            LegacyWorkloadRequirementsEditLinks,
        },
    })
    export default class LegacyWorkloadRequirementsBody extends Vue {
        @Prop({required: true}) public roster!: RosterData;
        @Prop({default: WorkloadRequirementSorting.Alphabet}) public sorting!: WorkloadRequirementSorting;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public resourceFunctionNames!: Record<number, string>;
        @Prop({type: Boolean}) public negativeOnly!: boolean;

        public getBeginTime(shift: Shift): number {
            let result: number = 31556926;
            if (shift.template!.specifications) {
                for (const specification of shift.template!.specifications) {
                    const beginTimeInSeconds: number = Dates.Time.parse(specification.begin);
                    if (beginTimeInSeconds < result) {
                        result = beginTimeInSeconds;
                    }
                }
            }

            return result;
        }

        // getter for orderedshift ids for workload requirement | array shiftsIds sorted by date
        public get orderedShiftIds(): number[] {
            // use Set to make sure we're dealing with distinct values (it's likely that there is some/much overlap between the two sources)
            const usedShiftIds: number[] = [...new Set(Object.keys(this.workloadRequirements).concat(Object.keys(this.roster.openShifts!)).map(Number))];

            if (Object.keys(this.shifts).length === 0) {
                return usedShiftIds;
            }

            if (this.sorting === WorkloadRequirementSorting.Alphabet) {
                return usedShiftIds.sort(this.sortShiftsAlphabetically);
            } else {
                return usedShiftIds.sort(this.sortShiftsByBeginTime);
            }
        }

        public sortShiftsAlphabetically(shiftIdA: number, shiftIdB: number): number {
            const shiftA: Shift = this.shifts[shiftIdA];
            const shiftB: Shift = this.shifts[shiftIdB];

            const textOrder: number = shiftA.template!.code.localeCompare(shiftB.template!.code);
            if (textOrder === 0) {
                const shiftACompare: number = this.getBeginTime(shiftA);
                const shiftBCompare: number = this.getBeginTime(shiftB);

                return Math.sign(shiftACompare - shiftBCompare);
            }

            return textOrder;
        }

        public sortShiftsByBeginTime(shiftIdA: number, shiftIdB: number): number {
            const shiftA: Shift = this.shifts[shiftIdA];
            const shiftB: Shift = this.shifts[shiftIdB];

            const shiftACompare: number = this.getBeginTime(shiftA);
            const shiftBCompare: number = this.getBeginTime(shiftB);

            if (shiftACompare === shiftBCompare) {
                return shiftA.template!.code.localeCompare(shiftB.template!.code);
            }

            return Math.sign(shiftACompare - shiftBCompare);
        }

        public get workloadRequirements(): Record<string, LegacyWorkloadRequirement> {
            return this.roster.workloadRequirements!;
        }

        public get resources(): PlanbordResource[] {
            return this.roster.rosterResources;
        }

        public get resourcesFromOtherRosters(): ResourceWithKeyedByDateObjects[] {
            return this.roster.resourcesFromOtherRosters;
        }

        public get relatedResources(): ResourceWithKeyedByDateObjects[] {
            return this.roster.relatedResources;
        }

        public get workloadTree(): Record<number, number[]> {
            return WorkloadTree.create(this.shifts);
        }

        public get workloadCounts(): Record<number, Record<string, WorkloadCount>> {
            // Define placeholder to return
            const counter: WorkloadCounter = new WorkloadCounter(this.workloadTree, this.organisationDeploymentStates);
            const startDate: Date = new Date(this.roster.days[0].date);
            const endDate: Date = new Date(this.roster.days[this.roster.days.length - 1].date);

            for (const shiftId in this.workloadRequirements) {
                counter.initializeShift(Number(shiftId));
            }

            const allResources: ResourceWithKeyedByDateObjects[] = (this.resources as ResourceWithKeyedByDateObjects[]).concat(this.resourcesFromOtherRosters).concat(this.relatedResources);

            // Loop over deployments
            for (const resource of allResources) {
                for (const date in resource.deployments) {
                    for (const deployment of resource.deployments[date]) {
                        counter.incrementCount(deployment.shiftId, deployment.stateId, deployment.date, resource.functions);
                    }
                }

                if (resource.personalStamps) {
                    for (const stamp of resource.personalStamps) {
                        if (stamp.personalStampShifts) {
                            const minimumDate: Date = stamp.beginDate > startDate ? stamp.beginDate : startDate;
                            const maximumDate: Date = stamp.endDate && stamp.endDate < endDate ? stamp.endDate : endDate;
                            for (const shift of stamp.personalStampShifts) {
                                for (const date of this.getStampShiftDates(shift, stamp, minimumDate, maximumDate)) {
                                    if (!resource.stampDeployedUntil || date > resource.stampDeployedUntil) {
                                        counter.incrementCount(shift.shiftId, undefined, date, resource.functions);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            for (const company of Object.values(this.roster.companies)) {
                for (const date in company.deploymentsWithoutEmployee) {
                    for (const companyDeployment of company.deploymentsWithoutEmployee[date]) {
                        counter.incrementCount(companyDeployment.shiftId, companyDeployment.stateId, companyDeployment.date);
                    }
                }
                for (const companyEmployee of Object.values(company.employees)) {
                    for (const date in companyEmployee.deployments) {
                        for (const companyEmployeeDeployment of companyEmployee.deployments[date]) {
                            counter.incrementCount(companyEmployeeDeployment.shiftId, companyEmployeeDeployment.stateId, companyEmployeeDeployment.date);
                        }
                    }
                }
            }

            // Return created object
            return counter.counts;
        }

        public getStampShiftDates(shift: PersonalStampShift, stamp: PersonalStamp, minimumDate: Date, maximumDate: Date): Date[] {
            // some math:
            // we need to start at the date the shift first is encountered after minimumDate
            // so: we calculate the difference between the stamp start date and the minimumDate
            // and divide it by the stamp iteration length (rounded down)
            // then we add the stamp dayIndex and continuously increase by the stamp length
            // we may end up just before the minimumDate (do the math)
            // in which case we will simply ignore the date
            // we return the result as soon as we've passed the maximumDate
            // the result MAY be empty (two week stamp only one week visible, shift is outside of that week)

            const result: Date[] = [];
            const dayDiff: number = Dates.Utils.diffInDays(minimumDate, stamp.beginDate);
            const iterationDiff: number = Math.floor(dayDiff / stamp.iterationLength);
            const date: Date = Dates.Utils.addDays(stamp.beginDate, iterationDiff * stamp.iterationLength + shift.dayIndex);
            while (date <= maximumDate) {
                if (date >= minimumDate) {
                    result.push(new Date(date));
                }
                date.setDate(date.getDate() + stamp.iterationLength);
            }

            return result;
        }
    }
</script>
